import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button, Modal, Skeleton, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { saveBlob } from '@/lib/download';
import { formatDateTime } from '@/lib/format';

/**
 * Opens an attachment INSIDE the app — owner's decision, 2026-09-25: "render
 * the attachment in app in a modal while trying to open it", with every type
 * that can reasonably be shown rendered, and a link embedded with an
 * always-visible "Open in new tab" fallback.
 *
 * ONE component for company AND task attachments. The two panels hand it a
 * loader for the bytes; it never calls the API itself, so it cannot fetch the
 * wrong resource's download route.
 *
 * ⚠️ **What renders how**, decided by {@link viewKindOf}:
 *
 * | kind | how |
 * | --- | --- |
 * | PDF | the browser's own viewer, in an iframe over an object URL |
 * | PNG, JPG, GIF, WebP | `<img>` |
 * | HEIC, HEIF | converted to JPEG in the browser (`heic2any`), then `<img>` |
 * | text, markdown | `<pre>` |
 * | CSV, XLSX, XLS | a table per sheet (SheetJS), capped — see `MAX_ROWS` |
 * | DOCX | HTML (`mammoth`) in a sandboxed frame |
 * | HTML, HTM | the file's own document in the same sandboxed frame |
 * | EML | headers + body (`postal-mime`) |
 * | MSG | headers + body (`@kenjiuno/msgreader`) |
 * | anything else | a sentence and the Download button |
 *
 * Every library is `import()`ed on demand, so none of them is in the main
 * bundle; a reader who never opens a spreadsheet never downloads SheetJS.
 *
 * 🔴 **HTML FROM A FILE IS UNTRUSTED AND IS NEVER PUT IN THIS DOCUMENT.** An
 * email body and a converted Word file render in an `<iframe srcdoc>` with
 * `sandbox` (no scripts, no same-origin) and a CSP that loads nothing from the
 * network. That also means images hosted elsewhere are not fetched — a remote
 * image in an email is usually a tracking pixel, and loading it would tell the
 * sender who opened it and from where.
 *
 * An `.html` file (2026-10-23: the email trails SEED EMERGE writes onto a
 * company's attachments are standalone HTML documents) goes through the SAME
 * frame. It is a whole document, so it is not wrapped in ours: our CSP `<meta>`
 * is put in FRONT of it ({@link sandboxStandaloneDocument}) so it is the first
 * thing the parser sees and governs everything after it. The file's own CSP, if
 * any, can only narrow ours further.
 */

export type AttachmentViewerSubject =
  | {
      kind: 'file';
      title: string;
      fileName: string | null;
      contentType: string | null;
      /** Fetches the bytes. Called once per open. */
      load: (signal: AbortSignal) => Promise<{ filename: string; blob: Blob }>;
    }
  | { kind: 'link'; title: string; url: string };

type ViewKind =
  | 'pdf'
  | 'image'
  | 'heic'
  | 'text'
  | 'sheet'
  | 'docx'
  | 'html'
  | 'eml'
  | 'msg'
  | 'audio'
  | 'video'
  | 'none';

const EXTENSION_KIND: Record<string, ViewKind> = {
  pdf: 'pdf',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  heic: 'heic',
  heif: 'heic',
  txt: 'text',
  md: 'text',
  csv: 'sheet',
  xlsx: 'sheet',
  xls: 'sheet',
  docx: 'docx',
  html: 'html',
  htm: 'html',
  eml: 'eml',
  msg: 'msg',
  mp3: 'audio',
  m4a: 'audio',
  wav: 'audio',
  mp4: 'video',
  webm: 'video',
};

const TYPE_KIND: Record<string, ViewKind> = {
  'application/pdf': 'pdf',
  'image/png': 'image',
  'image/jpeg': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'image/heic': 'heic',
  'image/heif': 'heic',
  'text/plain': 'text',
  'text/markdown': 'text',
  'text/csv': 'sheet',
  'text/html': 'html',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'sheet',
  'application/vnd.ms-excel': 'sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'message/rfc822': 'eml',
  'application/vnd.ms-outlook': 'msg',
};

/**
 * The stored content type first, the file name's extension second. The type is
 * what the server stored; the extension rescues a row stored under a generic
 * type, and a `.csv` Chrome on Windows reported as `application/vnd.ms-excel`
 * reads as a spreadsheet either way.
 */
function viewKindOf(contentType: string | null, fileName: string | null): ViewKind {
  const bare = (contentType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
  const byType = TYPE_KIND[bare];
  if (byType !== undefined) return byType;
  if (bare.startsWith('audio/')) return 'audio';
  if (bare.startsWith('video/')) return 'video';
  const dot = fileName?.lastIndexOf('.') ?? -1;
  if (fileName && dot !== -1) {
    return EXTENSION_KIND[fileName.slice(dot + 1).toLowerCase()] ?? 'none';
  }
  return 'none';
}

/** A spreadsheet bigger than this is cut, and the cut is said. */
const MAX_ROWS = 500;
const MAX_COLUMNS = 50;

interface Sheet {
  name: string;
  rows: string[][];
  totalRows: number;
  totalColumns: number;
}

interface EmailAttachment {
  name: string;
  blob: Blob;
}

interface EmailView {
  subject: string;
  from: string;
  to: string;
  cc: string;
  date: string;
  html: string | null;
  text: string | null;
  attachments: EmailAttachment[];
}

type Rendered =
  | { kind: 'url'; view: 'pdf' | 'image' | 'audio' | 'video'; url: string }
  | { kind: 'text'; text: string }
  | { kind: 'sheets'; sheets: Sheet[] }
  | { kind: 'html'; html: string; standalone?: boolean }
  | { kind: 'email'; email: EmailView }
  | { kind: 'none' };

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; filename: string; blob: Blob; rendered: Rendered };

/* --- the HTML sandbox ------------------------------------------------------ */

/**
 * The CSP inside every rendered document: nothing from the network, inline
 * styles allowed (Word and email HTML are styled inline), images only from
 * `data:` and `blob:` (the cid images an email carries are turned into data
 * URLs before rendering).
 */
const SANDBOX_CSP = "default-src 'none'; img-src data: blob:; style-src 'unsafe-inline'; font-src data:";

function sandboxDocument(body: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${SANDBOX_CSP}"><base target="_blank"><style>body{font:14px/1.5 system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1a1a1a;background:#fff;margin:16px;word-wrap:break-word}img{max-width:100%;height:auto}table{border-collapse:collapse}td,th{vertical-align:top}</style></head><body>${body}</body></html>`;
}

/**
 * A file that is already a whole HTML document. Our charset, CSP and
 * `<base target="_blank">` go BEFORE its markup: a `<meta>` before `<html>`
 * opens the head, so ours are the head's first children, and the file's own
 * `<html>`/`<head>` tags then merge into that head rather than starting a new
 * one. Its own leading doctype is dropped (ours stays first, so the frame is
 * still in standards mode).
 */
function sandboxStandaloneDocument(html: string): string {
  const withoutDoctype = html.replace(/^﻿?\s*<!doctype[^>]*>/i, '');
  return `<!doctype html><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${SANDBOX_CSP}"><base target="_blank">${withoutDoctype}`;
}

/** Remote images are blocked by the CSP; the reader is told when one was. */
function hasRemoteImages(html: string): boolean {
  return /<img[^>]+src\s*=\s*["']?\s*https?:/i.test(html);
}

function SandboxedHtml({ html, title, standalone = false }: { html: string; title: string; standalone?: boolean }) {
  const doc = useMemo(() => (standalone ? sandboxStandaloneDocument(html) : sandboxDocument(html)), [html, standalone]);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {hasRemoteImages(html) && (
        <p className="m-0 text-xs text-fg-muted">Images hosted on other websites are not loaded.</p>
      )}
      <iframe
        title={title}
        srcDoc={doc}
        /*
         * Popups only, so a link inside the document opens in a new tab (the
         * `<base target="_blank">` above). No scripts, no same-origin: this
         * document can reach nothing of ours.
         */
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        className="min-h-[50svh] w-full flex-1 rounded-md border border-border bg-white"
      />
    </div>
  );
}

/* --- loaders --------------------------------------------------------------- */

function mailbox(value: { name?: string; address?: string } | undefined): string {
  if (!value) return '';
  const name = value.name?.trim() ?? '';
  const address = value.address?.trim() ?? '';
  if (name && address) return `${name} <${address}>`;
  return name || address;
}

function toBytes(content: ArrayBuffer | Uint8Array | string): Uint8Array<ArrayBuffer> {
  if (typeof content === 'string') return new TextEncoder().encode(content);
  if (content instanceof Uint8Array) return new Uint8Array(content);
  return new Uint8Array(content);
}

function base64Of(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

async function renderEml(blob: Blob): Promise<EmailView> {
  const { default: PostalMime } = await import('postal-mime');
  const email = await PostalMime.parse(blob);
  const flatten = (list: typeof email.to) =>
    (list ?? [])
      .flatMap((entry) => ('group' in entry && entry.group ? entry.group : [entry]))
      .map((entry) => mailbox(entry))
      .filter((part) => part !== '')
      .join(', ');

  let html = email.html ?? null;
  const attachments: EmailAttachment[] = [];
  for (const part of email.attachments) {
    const bytes = toBytes(part.content);
    /*
     * An inline image referenced from the body as `cid:…` becomes a data URL
     * so it renders under the sandbox CSP. It is not also listed as a file.
     */
    const cid = part.contentId?.replace(/^<|>$/g, '');
    if (html && cid && html.includes(`cid:${cid}`)) {
      html = html.split(`cid:${cid}`).join(`data:${part.mimeType};base64,${base64Of(bytes)}`);
      continue;
    }
    attachments.push({
      name: part.filename?.trim() || 'attachment',
      blob: new Blob([bytes], { type: part.mimeType }),
    });
  }

  return {
    subject: email.subject?.trim() ?? '',
    from: mailbox(email.from && !('group' in email.from && email.from.group) ? email.from : undefined),
    to: flatten(email.to),
    cc: flatten(email.cc),
    date: email.date ? formatDateTime(email.date) : '',
    html,
    text: email.text ?? null,
    attachments,
  };
}

async function renderMsg(blob: Blob): Promise<EmailView> {
  const { default: MsgReader } = await import('@kenjiuno/msgreader');
  const reader = new MsgReader(await blob.arrayBuffer());
  const data = reader.getFileData();
  const recipients = (type: 'to' | 'cc') =>
    (data.recipients ?? [])
      .filter((entry) => (entry.recipType ?? 'to') === type)
      .map((entry) => mailbox({ name: entry.name, address: entry.smtpAddress ?? entry.email }))
      .filter((part) => part !== '')
      .join(', ');

  const attachments: EmailAttachment[] = [];
  for (const entry of data.attachments ?? []) {
    if (entry.attachmentHidden) continue;
    const attached = reader.getAttachment(entry);
    attachments.push({
      name: attached.fileName?.trim() || 'attachment',
      blob: new Blob([new Uint8Array(attached.content)]),
    });
  }

  const sent = data.clientSubmitTime ?? data.messageDeliveryTime;
  return {
    subject: data.subject?.trim() ?? '',
    from: mailbox({ name: data.senderName, address: data.senderSmtpAddress ?? data.senderEmail }),
    to: recipients('to'),
    cc: recipients('cc'),
    date: sent ? formatDateTime(sent) : '',
    html: data.bodyHtml ?? null,
    text: data.body ?? null,
    attachments,
  };
}

async function renderSheets(blob: Blob, isCsv: boolean): Promise<Sheet[]> {
  const XLSX = await import('xlsx');
  const workbook = isCsv
    ? XLSX.read(await blob.text(), { type: 'string', raw: true })
    : XLSX.read(await blob.arrayBuffer(), { type: 'array' });
  return workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name];
    const all = sheet
      ? (XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: '' }) as unknown[][])
      : [];
    const totalColumns = all.reduce((max, row) => Math.max(max, row.length), 0);
    return {
      name,
      totalRows: all.length,
      totalColumns,
      rows: all
        .slice(0, MAX_ROWS)
        .map((row) => row.slice(0, MAX_COLUMNS).map((cell) => (cell == null ? '' : String(cell)))),
    };
  });
}

async function render(kind: ViewKind, blob: Blob, contentType: string | null, fileName: string | null): Promise<Rendered> {
  switch (kind) {
    case 'pdf':
      return { kind: 'url', view: 'pdf', url: URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })) };
    case 'image':
    case 'audio':
    case 'video': {
      const type = contentType || blob.type;
      return { kind: 'url', view: kind, url: URL.createObjectURL(type ? new Blob([blob], { type }) : blob) };
    }
    case 'heic': {
      const { default: heic2any } = await import('heic2any');
      const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.9 });
      const first = Array.isArray(converted) ? converted[0] : converted;
      if (!first) throw new Error('The image could not be converted for display.');
      return { kind: 'url', view: 'image', url: URL.createObjectURL(first) };
    }
    case 'text':
      return { kind: 'text', text: await blob.text() };
    case 'sheet': {
      const isCsv =
        (fileName ?? '').toLowerCase().endsWith('.csv') ||
        (contentType ?? '').toLowerCase().startsWith('text/csv');
      return { kind: 'sheets', sheets: await renderSheets(blob, isCsv) };
    }
    case 'docx': {
      const mammoth = await import('mammoth');
      const result = await mammoth.convertToHtml({ arrayBuffer: await blob.arrayBuffer() });
      return { kind: 'html', html: result.value };
    }
    case 'html':
      return { kind: 'html', html: await blob.text(), standalone: true };
    case 'eml':
      return { kind: 'email', email: await renderEml(blob) };
    case 'msg':
      return { kind: 'email', email: await renderMsg(blob) };
    case 'none':
      return { kind: 'none' };
  }
}

/* --- pieces ---------------------------------------------------------------- */

function SheetView({ sheets }: { sheets: Sheet[] }) {
  const [index, setIndex] = useState(0);
  const sheet = sheets[index];
  if (!sheet) return <p className="m-0 text-sm text-fg-muted">This workbook has no sheets.</p>;
  const cut = sheet.totalRows > MAX_ROWS || sheet.totalColumns > MAX_COLUMNS;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {sheets.length > 1 && (
        <ViewToggle
          label="Sheet"
          className="max-w-full"
          value={String(index)}
          options={sheets.map((entry, i) => ({ value: String(i), label: entry.name }))}
          onChange={(next) => setIndex(Number(next))}
        />
      )}
      {cut && (
        <p className="m-0 text-xs text-fg-muted">
          Showing the first {Math.min(sheet.totalRows, MAX_ROWS)} of {sheet.totalRows} rows and{' '}
          {Math.min(sheet.totalColumns, MAX_COLUMNS)} of {sheet.totalColumns} columns. Download the file to
          see all of it.
        </p>
      )}
      {sheet.rows.length === 0 ? (
        <p className="m-0 text-sm text-fg-muted">This sheet is empty.</p>
      ) : (
        <TableWrap className="min-h-0 flex-1 overflow-auto" frozenHeader frozenMaxHeight="none">
          <Table>
            <THead>
              <TR>
                {(sheet.rows[0] ?? []).map((cell, i) => (
                  <TH key={i}>{cell}</TH>
                ))}
              </TR>
            </THead>
            <TBody>
              {sheet.rows.slice(1).map((row, r) => (
                <TR key={r}>
                  {row.map((cell, c) => (
                    <TD key={c} className="whitespace-pre-wrap">
                      {cell}
                    </TD>
                  ))}
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      )}
    </div>
  );
}

function HeaderLine({ label, value }: { label: string; value: string }) {
  if (value === '') return null;
  return (
    <div className="flex gap-2 text-xs">
      <dt className="w-14 shrink-0 text-fg-muted">{label}</dt>
      <dd className="m-0 min-w-0 text-fg [overflow-wrap:anywhere]">{value}</dd>
    </div>
  );
}

function EmailView({ email, title }: { email: EmailView; title: string }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="rounded-md border border-border bg-surface-sunken px-3 py-2">
        <p className="m-0 mb-1 text-sm font-semibold text-fg [overflow-wrap:anywhere]">
          {email.subject || '(no subject)'}
        </p>
        <dl className="m-0 flex flex-col gap-0.5">
          <HeaderLine label="From" value={email.from} />
          <HeaderLine label="To" value={email.to} />
          <HeaderLine label="Cc" value={email.cc} />
          <HeaderLine label="Date" value={email.date} />
        </dl>
        {email.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-fg-muted">Attached:</span>
            {email.attachments.map((file, i) => (
              <Button key={`${file.name}-${i}`} size="sm" variant="secondary" onClick={() => saveBlob(file.name, file.blob)}>
                {file.name}
              </Button>
            ))}
          </div>
        )}
      </div>
      {email.html ? (
        <SandboxedHtml html={email.html} title={title} />
      ) : email.text ? (
        <pre className="m-0 min-h-0 flex-1 overflow-auto rounded-md border border-border bg-surface p-3 text-xs whitespace-pre-wrap text-fg [overflow-wrap:anywhere]">
          {email.text}
        </pre>
      ) : (
        <p className="m-0 text-sm text-fg-muted">This email has no body.</p>
      )}
    </div>
  );
}

function isEmbeddable(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

/* --- the viewer ------------------------------------------------------------ */

/**
 * ⚠️ **`subject` must be STABLE** — held in the caller's state, not built inline
 * in render. The load effect keys on it, so a fresh object every render would
 * refetch the file every render.
 */
export function AttachmentViewer({
  subject,
  onClose,
}: {
  subject: AttachmentViewerSubject | null;
  onClose: () => void;
}) {
  /* Mounted fresh on every open, so no state carries over from the last file. */
  return subject === null ? null : <OpenViewer subject={subject} onClose={onClose} />;
}

function OpenViewer({ subject, onClose }: { subject: AttachmentViewerSubject; onClose: () => void }) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  /* Bytes that arrived but could not be rendered, so Download still works. */
  const [downloadable, setDownloadable] = useState<{ filename: string; blob: Blob } | null>(null);

  const file = subject.kind === 'file' ? subject : null;

  useEffect(() => {
    if (file === null) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;

    void (async () => {
      try {
        const { filename, blob } = await file.load(controller.signal);
        if (controller.signal.aborted) return;
        const kind = viewKindOf(file.contentType, file.fileName ?? filename);
        let rendered: Rendered;
        try {
          rendered = await render(kind, blob, file.contentType, file.fileName ?? filename);
        } catch {
          if (controller.signal.aborted) return;
          /*
           * A file whose bytes arrived but could not be READ — a damaged
           * workbook, an encrypted Word file — still offers Download. The
           * failure is about the viewer, not about the attachment. The parser's
           * own message is not shown: it is library internals ("end of central
           * directory"), not something a reader can act on.
           */
          setState({
            status: 'error',
            message: 'This file couldn’t be shown here. Download it to open it on your computer.',
          });
          setDownloadable({ filename, blob });
          return;
        }
        if (controller.signal.aborted) {
          if (rendered.kind === 'url') URL.revokeObjectURL(rendered.url);
          return;
        }
        if (rendered.kind === 'url') objectUrl = rendered.url;
        setState({ status: 'ready', filename, blob, rendered });
      } catch (caught) {
        if (controller.signal.aborted) return;
        setState({
          status: 'error',
          message: caught instanceof Error && caught.message ? caught.message : 'The file couldn’t be loaded.',
        });
      }
    })();

    return () => {
      controller.abort();
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
    // `attempt` re-runs the load on Retry.
  }, [file, attempt]);

  const bytes = state.status === 'ready' ? { filename: state.filename, blob: state.blob } : downloadable;

  const download = () => {
    if (bytes) saveBlob(bytes.filename, bytes.blob);
  };

  let body: ReactNode;
  let headerActions: ReactNode;

  if (subject.kind === 'link') {
    const embeddable = isEmbeddable(subject.url);
    headerActions = (
      <Button size="sm" variant="secondary" href={subject.url} target="_blank" rel="noreferrer noopener">
        Open in new tab
      </Button>
    );
    body = embeddable ? (
      <div className="flex h-[70svh] flex-col gap-2">
        <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
          {subject.url} — many sites refuse to be shown inside another page; if nothing appears below, use
          Open in new tab.
        </p>
        <iframe
          title={subject.title}
          src={subject.url}
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
          className="min-h-0 w-full flex-1 rounded-md border border-border bg-white"
        />
      </div>
    ) : (
      <p className="m-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
        {subject.url} can’t be shown inside SEED EMERGE. Use Open in new tab.
      </p>
    );
  } else {
    headerActions = (
      <Button size="sm" variant="secondary" onClick={download} disabled={bytes === null}>
        Download
      </Button>
    );
    if (state.status === 'loading') {
      body = (
        <div className="flex h-[70svh] flex-col gap-3" aria-busy="true">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="w-full flex-1" />
        </div>
      );
    } else if (state.status === 'error') {
      body = (
        <div className="flex flex-col items-start gap-2">
          <Notice tone="danger" role="alert" className="w-full [overflow-wrap:anywhere]">
            {state.message}
          </Notice>
          {downloadable === null && (
            <Button size="sm" variant="secondary" onClick={() => {
                setState({ status: 'loading' });
                setAttempt((n) => n + 1);
              }}
            >
              Try again
            </Button>
          )}
        </div>
      );
    } else {
      const rendered = state.rendered;
      const fileLine =
        subject.fileName && subject.fileName !== subject.title ? (
          <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">{subject.fileName}</p>
        ) : null;
      let content: ReactNode;
      switch (rendered.kind) {
        case 'url':
          content =
            rendered.view === 'pdf' ? (
              <iframe title={subject.title} src={rendered.url} className="min-h-0 w-full flex-1 rounded-md border border-border bg-white" />
            ) : rendered.view === 'image' ? (
              <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-md border border-border bg-surface-sunken p-2">
                <img src={rendered.url} alt={subject.title} className="max-h-full max-w-full object-contain" />
              </div>
            ) : rendered.view === 'audio' ? (
              <audio controls src={rendered.url} className="w-full" />
            ) : (
              <video controls src={rendered.url} className="min-h-0 w-full flex-1 rounded-md bg-black" />
            );
          break;
        case 'text':
          content = (
            <pre className="m-0 min-h-0 flex-1 overflow-auto rounded-md border border-border bg-surface p-3 text-xs whitespace-pre-wrap text-fg [overflow-wrap:anywhere]">
              {rendered.text}
            </pre>
          );
          break;
        case 'sheets':
          content = <SheetView sheets={rendered.sheets} />;
          break;
        case 'html':
          content = <SandboxedHtml html={rendered.html} title={subject.title} standalone={rendered.standalone === true} />;
          break;
        case 'email':
          content = <EmailView email={rendered.email} title={subject.title} />;
          break;
        case 'none':
          content = (
            <div className="flex flex-col items-start gap-2">
              <p className="m-0 text-sm text-fg-secondary">This type of file can’t be shown here.</p>
              <Button size="sm" onClick={download}>
                Download
              </Button>
            </div>
          );
          break;
      }
      body = (
        <div className={rendered.kind === 'none' ? 'flex flex-col gap-2' : 'flex h-[70svh] flex-col gap-2'}>
          {fileLine}
          {content}
        </div>
      );
    }
  }

  return (
    <Modal open onClose={onClose} title={subject.title} size="xl" headerActions={headerActions}>
      {body}
    </Modal>
  );
}
